import React from 'react';
import { Award, Users, CheckCircle2, ShieldCheck, Sprout, Lightbulb } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { whyChooseUs } from '../data/mock';

const iconMap = {
  Award,
  Users,
  CheckCircle2,
  ShieldCheck,
  Sprout,
  Lightbulb
};

const WhyChooseUs = () => {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
            Why Choose Us
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Trusted Partner in{' '}
            <span className="text-emerald-600">Engineering Excellence</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We combine technical expertise, collaborative partnerships, and unwavering 
            commitment to deliver exceptional results on every project.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {whyChooseUs.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <Card
                key={feature.id}
                className="group border-none shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50 overflow-hidden relative"
                style={{
                  animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`
                }}
              >
                {/* Decorative Element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                
                <CardContent className="p-8 relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                    {Icon && <Icon className="w-8 h-8 text-white" />}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-200">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Highlight Section */}
        <div className="mt-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 rounded-3xl p-12 text-white shadow-2xl relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                End-to-End Solutions Through Strategic Partnerships
              </h3>
              <p className="text-emerald-50 text-lg leading-relaxed mb-8">
                Our collaborative approach with trusted industry partners ensures you receive 
                comprehensive, multidisciplinary solutions tailored to your unique requirements. 
                From initial concept to project completion, we're with you every step of the way.
              </p>
              <div className="grid sm:grid-cols-3 gap-8 mt-12">
                {[
                  { number: '100%', label: 'Client-Focused' },
                  { number: '24/7', label: 'Support Available' },
                  { number: 'ISO', label: 'Certified Quality' }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold mb-2">{item.number}</div>
                    <div className="text-emerald-100 text-sm">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
